import { useEffect, useState } from "react";
import { Compass, Focus, Hourglass, Smartphone, CalendarX, Repeat, BatteryLow, Brain, Layers, Signpost, Sprout, CircleHelp } from "lucide-react";
import { OBSTACLE_OPTIONS, OBSTACLE_NONE_STRUGGLING } from "./onboardingData";
import { loadOnboardingDraft, saveOnboardingDraft, clearOnboardingDraft } from "./onboardingStorage";
import OnboardingSection2 from "./OnboardingSection2";
import OnboardingFieldFlow from "./OnboardingSection3";
import OnboardingScreen, { PreviewPager } from "./OnboardingChrome";
import {
  contentArea, bottomBar, optionBtn, primaryBtn, heading, subline,
} from "./onboardingStyles";
import { isFirebaseConfigured } from "../firebase/config";
import { useAuth } from "../firebase/auth";
import LoginScreen from "../firebase/LoginScreen";

// ─── ONBOARDING QUIZ ───────────────────────────────────────────────────────────
// Section 1 ("Getting to Know You") lives here, along with the transition
// screen. Section 2 ("Personalize") is OnboardingSection2.jsx, since it needs
// its own set of app setters (categories/habits/weekStart/plannerView/Google
// Calendar) and is large enough to warrant its own file.
//
// The whole flow's progress (which screen, answers so far) is mirrored into
// localStorage on every change via saveOnboardingDraft/loadOnboardingDraft —
// Section 2's "connect Google Calendar" checkbox triggers a real full-page
// OAuth redirect, and without this the round trip back would drop the user
// back at question 1 with everything they'd entered lost.
//
// Navigation (which screen, and the forward/back slide direction) is owned
// here and threaded down into Section 2, since going Back from Section 2's
// first screen needs to land on Section 1's Obstacles screen, and Back from
// that screen needs to land on the transition screen — decisions that only
// make sense from above both sections.
const emptyAnswers = {
  name: "",
  obstacles: [],
  weekStart: "sunday",
  plannerView: "calendar",
  categories: [],
  educationLevel: null,
  collegeField: null,
  collegeCourse: null,
  collegeCourseOther: "",
  workIndustry: null,
  workJob: null,
  workJobOther: "",
};


// Short app-preview pages shown BEFORE the quiz — a quick "here's what
// Clarity does" swipe-through, Storyset illustrations (credited in
// Settings > Credits), matching Clarity's pink where the source art
// already used it and left as-is elsewhere per product call.
// Each page has an accent colour for its dot grid
// (Jade, 2026-10-11). A topic pill and feature chips were tried and removed:
// Jade doesn't want features named on these pages; an accent bar was tried too.
const PREVIEW_PAGES = [
  {
    src: "/illustrations/healthy-habit.svg", background: "linear-gradient(180deg,#E4D4FA,#F6EFFF)", accent: "#9466D6", ink: "#7442C4",
    headline: "Build habits that stick", lead: "Build habits", hl: "that stick",
  },
  {
    src: "/illustrations/time-management.svg", background: "linear-gradient(180deg,#D3F3DF,#F0FBF4)", accent: "#4FAE78", ink: "#2C8A56",
    headline: "See where your time goes", lead: "See where your", hl: "time goes",
  },
  {
    src: "/illustrations/statistics.svg", background: "linear-gradient(180deg,#FFDCEB,#FFF3F8)", accent: "#D47CA0", ink: "#C04E7C",
    headline: "Watch your progress build", lead: "Watch your", hl: "progress build",
  },
];
const PREVIEW_TOTAL = PREVIEW_PAGES.length;

// Fetch and decode every preview illustration up front (Jade, 2026-10-11): each
// <img> used to start loading only when its page appeared, so on a first visit
// the text showed at once and the picture popped in a moment later. Kept in a
// module-level array so the decoded images aren't garbage-collected.
const preloadedPreviews = [];
function preloadPreviewImages() {
  if (preloadedPreviews.length || typeof Image === "undefined") return;
  // The categories page's tile illustrations ride along, so they're decoded
  // well before that page shows.
  const catTiles = ["cat-hygiene", "cat-fitness", "cat-home", "cat-productivity", "cat-finance", "cat-work", "cat-art", "cat-school"]
    .map(id => ({ src: `/illustrations/categories/${id}.svg` }));
  for (const { src } of [...PREVIEW_PAGES, ...catTiles]) {
    const img = new Image();
    img.src = src;
    if (img.decode) img.decode().catch(() => {});
    preloadedPreviews.push(img);
  }
}

// Started when this module loads (with the app), not when onboarding mounts,
// so the first illustration is already decoded when its page appears, including
// on a Replay Guide from Settings.
preloadPreviewImages();

function OnboardingFlow({ onComplete, setHabits, setCategories, saveWeekStart, setPlannerView, gcal }) {
  // The saved draft is read ONCE, synchronously, and used as the initial state.
  // It used to be restored in an effect after mount, with the whole flow
  // rendering null until then — so on a fresh install the app's Habits tab
  // showed for a moment before the first onboarding page appeared.
  const [draft] = useState(() => loadOnboardingDraft());
  const [phase, setPhase] = useState(draft?.phase || "preview"); // "preview" | "s1" | "transition" | "s2" | "field" | "auth"
  // Clamped: a draft saved when there were more preview pages would otherwise
  // point past the end of PREVIEW_PAGES.
  const [previewStep, setPreviewStep] = useState(typeof draft?.previewStep === "number" ? Math.min(draft.previewStep, PREVIEW_TOTAL - 1) : 0);
  const [s1Step, setS1Step] = useState(typeof draft?.s1Step === "number" ? draft.s1Step : 0);
  const [s2Step, setS2Step] = useState(typeof draft?.s2Step === "number" ? draft.s2Step : 0);
  // Firebase-configured builds ask to sign in right after the preview pages
  // (phase "auth"), BEFORE the quiz, so the quiz's picks save to the account —
  // see AuthGate.jsx. Local-only builds (no Firebase) skip the auth phase.
  const { user } = useAuth();
  // Domains still owed a follow-up question after the categories screen —
  // populated from which of "Work"/"School" the user picked, in the order
  // those chips appear (see OnboardingSection2.jsx's CategoriesStep "Next").
  // phase "field" runs OnboardingFieldFlow fresh for fieldQueue[0]; when it
  // reports done, the domain is shifted off and the next one (if any) starts.
  const [fieldQueue, setFieldQueue] = useState(Array.isArray(draft?.fieldQueue) ? draft.fieldQueue : []);
  const [answers, setAnswers] = useState(draft?.answers ? { ...emptyAnswers, ...draft.answers } : emptyAnswers);
  const [direction, setDirection] = useState("forward");
  // Which transition style the NEXT screen mount should use — "slide" (same
  // section's dots), "fadeSlide" (crossing into/out of a differently-dotted
  // section), or "fade" (the transition screen's own dot-less appearance).
  // Set right alongside setDirection by whichever handler triggers the
  // navigation — see OnboardingChrome.jsx's SlideStep for how it's consumed.
  // "none" = the very first screen appears instantly, with no fade/slide-in.
  // Every later navigation sets its own anim before changing screen.
  const [anim, setAnim] = useState("none");

  useEffect(() => {
    saveOnboardingDraft({ phase, previewStep, s1Step, s2Step, fieldQueue, answers });
  }, [phase, previewStep, s1Step, s2Step, fieldQueue, answers]);

  const finish = () => {
    clearOnboardingDraft();
    onComplete(answers);
  };

  // Once sign-in succeeds while sitting on the auth page, move straight on to
  // the quiz — no extra tap needed.
  useEffect(() => {
    if (phase === "auth" && user) { setDirection("forward"); setAnim("fade"); setPhase("transition"); }
  }, [phase, user]);

  // Sign-in now comes right after the preview pages and BEFORE the quiz, so
  // everything the quiz adds (categories, starter habits) is written into the
  // signed-in account. Asking at the end used to lose the picks: signing in
  // ran the cloud sync, which replaced the on-device data with the account's.
  // Local-only builds (no Firebase) and already-signed-in users skip it.
  const afterPreview = () => {
    setDirection("forward"); setAnim("fade");
    setPhase(isFirebaseConfigured && !user ? "auth" : "transition");
  };

  // Every exit from the quiz (Skip personalization, Get Started, and the "no
  // categories picked" shortcut inside Section 2) ends up here. The user is
  // already signed in by now, so it just finishes.
  const finishOrAuth = () => finish();

  const goForward = (fn) => { setDirection("forward"); fn(); };
  // Flow order: preview → auth (sign in) → transition → s1 (Obstacles) → s2 (categories) →
  // field (conditional, only if Work and/or School was picked) → s2 again
  // (starter habits) → finish. The transition screen ("Personalize your
  // journey with Clarity") leads INTO the quiz. The field detour sits between
  // Section 2's categories screen and its habits screen, since habit
  // suggestions are the one thing that needs the field answer.
  const goBack = () => {
    setDirection("back");
    if (phase === "s2") {
      // s2Step 1 -> 0 (Starter Habits -> Categories) stays inside Section
      // 2's own dots (slide); s2Step 0 -> Section 1's Obstacles screen
      // crosses into a differently-dotted section (fadeSlide).
      if (s2Step > 0) { setAnim("slide"); setS2Step(s => s - 1); }
      else { setAnim("fadeSlide"); setPhase("s1"); }
      return;
    }
    if (phase === "s1") {
      // Obstacles is Section 1's only screen now, so its Back always leads
      // to the dot-less transition screen, which is always fade regardless
      // of direction (see OnboardingChrome.jsx's SlideStep comment).
      setAnim("fade");
      setPhase("transition");
      return;
    }
    if (phase === "transition") {
      setAnim("fade");
      setPhase("preview");
      setPreviewStep(PREVIEW_TOTAL - 1);
      return;
    }
  };

  // Preview pages navigate on their own (not through goBack/goForward's
  // phase-transition logic above) since they're self-contained — Next just
  // advances the index until the last page, whose button instead moves into
  // the dot-less transition screen (fade, not fadeSlide — see
  // OnboardingChrome.jsx's SlideStep comment).
  const previewNext = () => {
    setDirection("forward");
    if (previewStep >= PREVIEW_TOTAL - 1) { afterPreview(); return; }
    setAnim("slide");
    setPreviewStep(s => s + 1);
  };
  const previewSkip = afterPreview;

  // Back from the field flow's very first screen (of any domain in the
  // queue) returns to the categories screen rather than retreading an
  // earlier-completed domain — simple, and the user's earlier answers for
  // that domain are already saved.
  const backFromField = () => { setDirection("back"); setAnim("fadeSlide"); setPhase("s2"); setS2Step(0); };

  // Back from the sign-in page returns to the last preview page.
  const backFromAuth = () => { setDirection("back"); setAnim("fade"); setPhase("preview"); setPreviewStep(PREVIEW_TOTAL - 1); };

  const enterFieldFlow = (domains) => { setDirection("forward"); setAnim("fadeSlide"); setFieldQueue(domains); setPhase("field"); };

  const onFieldDomainDone = () => {
    setDirection("forward");
    setFieldQueue(q => {
      const rest = q.slice(1);
      // Last domain done -> leaving the Work/School picker back to Section 2
      // (fadeSlide); otherwise moving to the next domain (School -> Work)
      // stays inside the same picker section (slide).
      if (rest.length === 0) { setAnim("fadeSlide"); setPhase("s2"); setS2Step(1); }
      else setAnim("slide");
      return rest;
    });
  };

  if (phase === "preview") {
    const isLast = previewStep === PREVIEW_TOTAL - 1;
    // Swipe between the pages (PreviewPager); no back button (Jade, 2026-10-26).
    return (
      <PreviewPager
        pages={PREVIEW_PAGES} index={previewStep}
        onIndexChange={(i) => { setDirection(i > previewStep ? "forward" : "back"); setAnim("slide"); setPreviewStep(i); }}
        onSkip={previewSkip} skipLabel="Skip"
        footer={
          <div style={bottomBar}>
            <button style={primaryBtn(false)} onClick={previewNext}>{isLast ? "Get Started" : "Next"}</button>
          </div>
        }
        renderPage={(page) => (
          <div style={{ ...contentArea, flex: 1, justifyContent: "flex-start", alignItems: "center", textAlign: "center", paddingTop: 18 }}>
            {/* Headline on top (Jade, 2026-10-11): two lines, big and tight, the
                second line in the page's colour (a highlighter stroke was tried
                and removed). */}
            <h1 aria-label={page.headline} style={{
              margin: 0, fontSize: 31, fontWeight: 800, lineHeight: 1.18, letterSpacing: -0.4,
              color: "#1E1A2E", maxWidth: 320,
            }}>
              <span style={{ display: "block" }}>{page.lead}</span>
              <span style={{ display: "block", color: page.ink }}>{page.hl}</span>
            </h1>
            {/* Illustration centred in the space left under the headline (a
                square card and then a circle behind it were tried and removed). */}
            <div style={{ flex: 1, minHeight: 0, width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={page.src} alt="" decoding="sync" draggable={false} style={{
                width: "100%", maxWidth: 360, maxHeight: "100%", objectFit: "contain", display: "block",
                // No filter here: the thick shadow under the big background shape is
                // drawn inside the SVG files themselves (filter "clarity-blob-shadow"),
                // so the small objects get none (Jade, 2026-10-11).
              }} />
            </div>
          </div>
        )}
      />
    );
  }

  if (phase === "auth") {
    return <LoginScreen onBack={backFromAuth} />;
  }

  if (phase === "transition") {
    return (
      <TransitionScreen
        direction={direction}
        onBack={goBack}
        onDone={() => goForward(() => { setAnim("fade"); setPhase("s1"); setS1Step(0); })}
      />
    );
  }

  if (phase === "field") {
    return (
      <OnboardingFieldFlow
        key={fieldQueue[0]}
        domain={fieldQueue[0]}
        direction={direction}
        setDirection={setDirection}
        anim={anim}
        setAnim={setAnim}
        onBack={backFromField}
        answers={answers}
        setAnswers={setAnswers}
        onDomainDone={onFieldDomainDone}
      />
    );
  }

  if (phase === "s2") {
    return (
      <OnboardingSection2
        step={s2Step}
        setStep={setS2Step}
        direction={direction}
        setDirection={setDirection}
        anim={anim}
        setAnim={setAnim}
        onBack={goBack}
        answers={answers}
        setAnswers={setAnswers}
        onFinish={finishOrAuth}
        onEnterFieldFlow={enterFieldFlow}
        setHabits={setHabits}
        setCategories={setCategories}
      />
    );
  }

  // ── Section 1 (just Obstacles now — Discovery/"How'd you find us?" was
  // removed, and the transition screen leads in ahead of this instead of
  // following it) ──
  const set = (patch) => setAnswers(a => ({ ...a, ...patch }));
  const next = () => goForward(() => { setAnim("fadeSlide"); setPhase("s2"); setS2Step(0); });

  // Section 1 is a single question, so its lone progress dot says nothing:
  // total 0 hides the dots row (the header stays balanced either way).
  const screenProps = { direction, anim, progress: s1Step + 1, total: 0, onBack: goBack };

  return (
    <OnboardingScreen {...screenProps} stepKey="s1-0" footer={
      <div style={bottomBar}>
        <button style={primaryBtn(answers.obstacles.length === 0)} disabled={answers.obstacles.length === 0} onClick={next}>Next</button>
      </div>
    }>
      <ObstacleStep obstacles={answers.obstacles} onChange={(obstacles) => set({ obstacles })} />
    </OnboardingScreen>
  );
}

// Multi-select — pick as many as apply. "None of these" is exclusive:
// picking it clears everything else, and picking anything else clears it.
// One icon per obstacle, shown in a soft circle on the left of its row
// (Jade, 2026-10-11). Keyed by OBSTACLE_OPTIONS id.
const OBSTACLE_ICONS = {
  distraction: Focus, procrastination: Hourglass, doomscrolling: Smartphone,
  "losing-track": CalendarX, "no-routine": Repeat, burnout: BatteryLow,
  overthinking: Brain, juggling: Layers, "no-start": Signpost,
  "sticking-long-term": Sprout, none: CircleHelp,
};
const OB_ICON_ACCENT = "#6F8BE0";

function ObstacleStep({ obstacles, onChange }) {
  const allOptions = [...OBSTACLE_OPTIONS, OBSTACLE_NONE_STRUGGLING];

  const toggle = (id) => {
    if (id === "none") { onChange(["none"]); return; }
    const cur = obstacles.filter(x => x !== "none");
    onChange(cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };

  return (
    <div style={contentArea}>
      <div>
        <div style={heading}>Do you experience any of the following?</div>
        <div style={subline}>Pick as many as apply, no wrong answer</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {allOptions.map((o) => {
          const active = obstacles.includes(o.id);
          const Icon = OBSTACLE_ICONS[o.id] || CircleHelp;
          return (
            <button key={o.id} style={{ ...optionBtn(active), padding: "14px 18px 14px 14px", boxShadow: "0 6px 18px rgba(111,139,224,0.30), 0 1px 3px rgba(111,139,224,0.20)" }} onClick={() => toggle(o.id)}>
              <span style={{
                flexShrink: 0, width: 40, height: 40, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: active ? "#fff" : "#EAF0FF", color: OB_ICON_ACCENT,
              }}>
                <Icon size={20} strokeWidth={2.2} />
              </span>
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Transition screen: fades its message in, holds, then fades on its own into
// Section 1's Obstacles screen — no tap needed. Back returns to the last
// preview/auth step. The "Skip personalization" button was removed (Jade,
// 2026-10-11) and the screen dressed up: a compass ("journey") in a glowing
// circle, three loading dots in the logo colours and a bar that fills over the
// hold, so the wait reads as "getting ready" rather than a pause. Plain white
// page, no logo and no subline (Jade).
const TRANSITION_HOLD_MS = 2400;
const TRANSITION_CSS = `
@keyframes obTransDot { 0%, 80%, 100% { transform: translateY(0); opacity: .45; } 40% { transform: translateY(-7px); opacity: 1; } }
@keyframes obTransFill { from { transform: scaleX(0); } to { transform: scaleX(1); } }
@keyframes obTransFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
@media (prefers-reduced-motion: reduce) { .ob-trans-anim { animation: none !important; } }
`;
const TRANSITION_COLORS = ["#BFD3FA", "#DAC8F7", "#F4BCD2"];

function TransitionScreen({ direction, onBack, onDone }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 50);
    const t2 = setTimeout(() => setVisible(false), TRANSITION_HOLD_MS);
    const t3 = setTimeout(() => onDone(), TRANSITION_HOLD_MS + 500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []); // eslint-disable-line

  return (
    <OnboardingScreen
      direction={direction} anim="fade" progress={0} total={0} onBack={onBack} stepKey="transition"
    >
      <style>{TRANSITION_CSS}</style>
      <div style={{
        ...contentArea, flex: 1, justifyContent: "center", alignItems: "center", textAlign: "center",
        opacity: visible ? 1 : 0, transition: "opacity 0.55s ease",
      }}>
        <div className="ob-trans-anim" style={{ position: "relative", width: 112, height: 112, marginBottom: 10, animation: "obTransFloat 4s ease-in-out infinite" }}>
          <div style={{
            position: "absolute", inset: -14, borderRadius: "50%",
            background: `linear-gradient(135deg, ${TRANSITION_COLORS.join(", ")})`,
            filter: "blur(18px)", opacity: 0.75,
          }} />
          <div style={{
            position: "relative", width: "100%", height: "100%", borderRadius: "50%",
            background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 10px 30px rgba(150,120,200,0.2)",
          }}>
            <Compass size={52} strokeWidth={1.8} color="#A98BE0" />
          </div>
        </div>
        <div style={{ fontSize: 27, fontWeight: 700, color: "var(--c-text-accent, #333)", lineHeight: 1.35, maxWidth: 320 }}>
          Personalize your journey with Clarity
        </div>
        {/* Progress bar right under the line (the three bouncing dots were removed, Jade 2026-10-11). */}
        <div style={{ width: 160, height: 6, borderRadius: 999, background: "#EEEAF8", overflow: "hidden", marginTop: 14 }}>
          <div className="ob-trans-anim" style={{
            height: "100%", borderRadius: 999, transformOrigin: "left",
            background: `linear-gradient(90deg, ${TRANSITION_COLORS.join(", ")})`,
            animation: `obTransFill ${TRANSITION_HOLD_MS}ms linear both`,
          }} />
        </div>
      </div>
    </OnboardingScreen>
  );
}

export default OnboardingFlow;
